import { useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import { useBusy } from '../../hooks/useBusy';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { useI18n, formatDay } from '../../i18n';
import { useLabels, countLabel } from '../../i18n/labels';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalBody from '../shared/ModalBody';
import ModalFooter from '../shared/ModalFooter';
import StagePill from '../bout/StagePill';
import { GripIcon } from '../shared/Icons';
import { cornerName } from '../../utils/personName';
import type { ApiWarning } from '../../hooks/useApiCall';
import type { Ring } from '../../types';
import { successText } from '../../i18n/labels';

/** Row shape consumed by the manual-assign grid — a denormalised view that
 *  covers both the "create manual bouts from draft brackets" flow
 *  (bout_id undefined) and the "reassign existing bouts" flow (bout_id
 *  present). Mirrors what `getBracketBouts` returns per item. */
export interface ManualBoutRow {
  bout_id?: string;       // only in edit mode
  bracket_id: string;
  bout_idx?: number;
  bout_number?: number;
  ring_id?: string;
  bout_date?: string;
  stage?: string;
  gender?: string;
  age_group?: string;
  weight_class?: string;
  /** The name parts of each corner; the grid formats them (#479). */
  red_first_name?: string | null;
  red_last_name?: string | null;
  blue_first_name?: string | null;
  blue_last_name?: string | null;
  red_source_idx?: number | null;
  blue_source_idx?: number | null;
  /** #552 — reassign mode: the bout whose winner fills the corner. The grid
   *  shows "Bout {n} winner" with the CURRENT number of that row. */
  red_source_bout_id?: string | null;
  blue_source_bout_id?: string | null;
}

interface Props {
  bouts: ManualBoutRow[];
  rings: Ring[];
  days: string[];
  tournamentId: string;
  onClose: () => void;
  /** The write committed (#664): the parent closes the modal and reads the
   *  bouts and the brackets again. The read rejects when it fails. */
  onSaved: () => Promise<unknown>;
  onBoutsChange: (bouts: ManualBoutRow[]) => void;
}

export default function ManualBoutAssignModal({ bouts, rings, days, tournamentId, onClose, onSaved, onBoutsChange }: Props) {
  const { showError } = useToast();
  const { confirm } = useConfirm();
  const afterWrite = useAfterWrite();
  const { t, lang } = useI18n();
  const { divisionLabel, boutCount, createBoutsLabel } = useLabels();
  // #605, #664 — the lock of Save: a second tap sends no second write.
  const creating = useBusy();

  // Row reorder via native HTML5 drag. Enabled only in edit (reassign) mode —
  // create mode rows carry source_idx references keyed on bracket position,
  // so global renumbering would break bracket advancement links.
  const isEditMode = !!bouts[0]?.bout_id;
  // The label of Save, and of the confirm button of its question (#665 review).
  const saveLabel = isEditMode
    ? countLabel(lang, bouts.length, { one: t.manualBouts.saveBoutsOne, few: t.manualBouts.saveBoutsFew, other: t.manualBouts.saveBoutsOther })
    : createBoutsLabel(bouts.length);
  const [dragIndex,     setDragIndex]     = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const reorder = (from: number, to: number) => {
    if (from === to) return;
    const next = [...bouts];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onBoutsChange(next.map((b, idx) => ({ ...b, bout_number: idx + 1 })));
  };

  async function save() {
    const isEdit = !!bouts[0]?.bout_id;
    if (creating.busy) return;
    if (!(await confirm({ message: isEdit ? t.tournament.saveReassignConfirm : t.tournament.createAllBoutsConfirm, confirmLabel: saveLabel }))) return;
    await creating.run(async () => {
      try {
        // UI disables the submit button until every row has
        // bout_id (edit mode) / bracket_id + bout_number (create
        // mode), so the cast to the strict DTO shape is safe here.
        let success: string;
        let warnings: ApiWarning[] | undefined;
        if (isEdit) {
          const { reassignBouts } = await import('../../api/client');
          const res = await reassignBouts(tournamentId, bouts as unknown as import('../../types').ReassignBoutAssignment[]);
          success = successText(res.data);
          warnings = res.data.warnings;
        } else {
          const { createManualBouts } = await import('../../api/client');
          const res = await createManualBouts(tournamentId, bouts as unknown as import('../../types').ManualBoutAssignment[]);
          success = successText(res.data);
        }
        // #664 — the write committed. The reads that follow are not part of it:
        // before, they sat in this `try`, and a failed read showed an error, kept
        // this modal open and let a second Save say that the bouts exist. A failed
        // read is a warning now (#419), and the parent has closed the modal.
        await afterWrite({ source: 'ManualBoutAssignModal:save', success, warnings, refresh: onSaved });
      } catch (err) { showError(err); }
    });
  }

  const title = isEditMode ? t.tournament.reassignBouts : t.tournament.manualAssignment;
  // The empty corner of a bout that a winner fills: its source, by number.
  const winnerOf = (row: ManualBoutRow, side: 'red' | 'blue'): string => {
    const srcId = side === 'red' ? row.red_source_bout_id : row.blue_source_bout_id;
    const src = srcId ? bouts.find(x => x.bout_id === srcId) : undefined;
    if (src?.bout_number) return t.common.boutWinner.replace('{n}', String(src.bout_number));
    const idx = side === 'red' ? row.red_source_idx : row.blue_source_idx;
    return idx != null ? t.common.boutWinner.replace('{n}', String(idx + 1)) : '—';
  };

  // #552 — the shared panel. The main action was in the header and the close
  // was an unnamed outline button; on a phone the 11-column table made the
  // whole dialog scroll sideways and took the header with it. The table now
  // scrolls inside its own wrapper.
  return (
    <ModalBackdrop onClose={onClose} maxWidth="5xl" layout="panel" ariaLabel={title}>
      <ModalHeader title={title} subtitle={boutCount(bouts.length)} />
      <ModalBody>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-xs">
            <thead>
              <tr className="bg-gray-50 text-left text-[10px] text-gray-500 uppercase tracking-wide border-b">
                <th className="px-1 py-2 w-6"></th>
                <th className="px-2 py-2 w-16">{t.manualBouts.colBout}</th>
                <th className="px-2 py-2 w-28">{t.common.ring}</th>
                <th className="px-2 py-2 w-28">{t.manualBouts.colDay}</th>
                <th className="px-2 py-2">{t.common.stage}</th>
                <th className="px-2 py-2">{t.common.gender}</th>
                <th className="px-2 py-2">{t.common.age}</th>
                <th className="px-2 py-2">{t.common.weight}</th>
                <th className="px-2 py-2">{t.common.red}</th>
                <th className="px-2 py-2 text-center">{t.common.vs}</th>
                <th className="px-2 py-2">{t.common.blue}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bouts.map((b, i) => (
                // The key must be unique for each row: a matched (Challenge) bout has
                // no bracket_id and no bout_idx, so two of them had the same key, and
                // a drag drew stale copies of the rows. A reassign row has its bout_id;
                // a planned row (create mode) is unique by bracket + index.
                <tr key={b.bout_id ?? `${b.bracket_id}-${b.bout_idx}`}
                    onDragOver={e => { if (isEditMode && dragIndex !== null) { e.preventDefault(); setDragOverIndex(i); } }}
                    onDragLeave={() => setDragOverIndex(prev => prev === i ? null : prev)}
                    onDrop={e => { e.preventDefault(); if (dragIndex !== null) reorder(dragIndex, i); setDragIndex(null); setDragOverIndex(null); }}
                    className={`${!b.bout_number || !b.ring_id ? 'bg-amber-50' : ''} ${dragIndex === i ? 'opacity-40' : ''} ${dragOverIndex === i && dragIndex !== null && dragIndex !== i ? 'outline-solid outline-2 outline-indigo-500 -outline-offset-2' : ''}`}>
                  <td draggable={isEditMode}
                      onDragStart={() => setDragIndex(i)}
                      onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                      className={`px-1 py-1.5 text-gray-400 select-none ${isEditMode ? 'cursor-move' : ''}`}
                      title={isEditMode ? t.manualBouts.dragTip : undefined}>
                    {isEditMode && <GripIcon className="w-4 h-4" />}
                  </td>
                  <td className="px-2 py-1.5">
                    <input type="number" min={1} className="input text-xs py-1 w-14" value={b.bout_number || ''}
                      onChange={e => onBoutsChange(bouts.map((x, j) => j === i ? { ...x, bout_number: parseInt(e.target.value) || 0 } : x))} />
                  </td>
                  <td className="px-2 py-1.5">
                    <select className="input text-xs py-1 w-full min-w-[6.5rem]" value={b.ring_id || ''} aria-label={t.common.ring}
                      onChange={e => onBoutsChange(bouts.map((x, j) => j === i ? { ...x, ring_id: e.target.value } : x))}>
                      <option value="">—</option>
                      {rings.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    {days.length > 1 ? (
                      <select className="input text-xs py-1 w-full min-w-[6.5rem]" value={b.bout_date || ''} aria-label={t.manualBouts.colDay}
                        onChange={e => onBoutsChange(bouts.map((x, j) => j === i ? { ...x, bout_date: e.target.value } : x))}>
                        <option value="">—</option>
                        {days.map(d => <option key={d} value={d}>{formatDay(d, 'EEE d')}</option>)}
                      </select>
                    ) : <span className="text-gray-400">{days[0] ? formatDay(days[0], 'EEE d') : '—'}</span>}
                  </td>
                  <td className="px-2 py-1.5">
                    <StagePill stage={b.stage} size="xs" />
                  </td>
                  <td className="px-2 py-1.5">{divisionLabel(b.gender ?? '')}</td>
                  <td className="px-2 py-1.5">{b.age_group}</td>
                  <td className="px-2 py-1.5">{b.weight_class}</td>
                  <td className="px-2 py-1.5 text-red-600 font-medium">{cornerName(b, 'red') || winnerOf(b, 'red')}</td>
                  <td className="px-2 py-1.5 text-center text-gray-400">{t.common.vs}</td>
                  <td className="px-2 py-1.5 text-blue-600 font-medium">{cornerName(b, 'blue') || winnerOf(b, 'blue')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ModalBody>
      <ModalFooter
        onSave={save}
        busy={creating.busy}
        saveLabel={saveLabel}
        saveDisabled={bouts.some(b => !b.bout_number || !b.ring_id)}
        onCancel={onClose}
      />
    </ModalBackdrop>
  );
}
